import { createRoot } from 'react-dom/client';
import Router from './components/routing/DashboardRouter';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { getPostHogConfig } from '@charitystack/shared';
import { TooltipProvider } from './components/ui/tooltip';
import { initSentry } from './sentry';
import { installAuthFetchDiagnostics } from './utils/authDiagnostics';
import { installChunkReloadListener } from './utils/chunkReload';
import App from './App';
import * as serviceWorker from './serviceWorker';
import { store } from './redux/store';
import { Provider } from 'react-redux';
import { Toaster } from 'sonner';
import { DashboardErrorBoundary } from './components/ErrorBoundary';
import { ClerkProvider } from '@clerk/clerk-react';
import { JobsProvider } from './contexts/JobsContext';
import { DashboardAssistantDockProvider } from './contexts/DashboardAssistantDockContext';
import DashboardAssistantProvider, {
} from './components/panels/DashboardAssistantProvider';


// Initialize Sentry FIRST (before React render)
initSentry();

// Stale-chunk auto-reload policy (vite:preloadError) — see utils/chunkReload.js.
installChunkReloadListener();
// Dev/staging: log JWT claim summary + response preview on 401/403 to AWS APIs (see authDiagnostics.js)
installAuthFetchDiagnostics();

// ============================================================================
// Clerk Configuration
// ============================================================================
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  console.error('VITE_CLERK_PUBLISHABLE_KEY is missing. Clerk authentication will not work.');
}

// Initialize PostHog
// NOTE: PostHogProvider will receive this client even if init fails.
// All components MUST use optional chaining when calling PostHog methods.
// Example: posthog?.get_session_id?.() NOT posthog.get_session_id()
if (typeof window !== 'undefined') {
  try {
    const apiKey = import.meta.env.VITE_POSTHOG_KEY;
    if (!apiKey) {
      console.warn('⚠️ VITE_POSTHOG_KEY not set - PostHog analytics disabled for dashboard');
    } else if (typeof Blob !== 'function') {
      console.warn('⚠️ Blob API not available - PostHog analytics disabled for dashboard');
    } else {
      posthog.init(apiKey, getPostHogConfig('dashboard'));
    }
  } catch (error) {
    console.error('⚠️ PostHog initialization failed for dashboard:', error);
    // App continues without analytics - this is non-blocking
  }
}

const container = document.getElementById('root');
const root = createRoot(container);

// Determine base path based on current location - calculated once at module load
// If accessed via /dashboard/, use that as basename
// Otherwise, use root /
// IMPORTANT: This is a constant calculated ONCE when the module loads to prevent
// re-calculation on every render which can cause Router to remount the entire app
const ROUTER_BASENAME = window.location.pathname.startsWith('/dashboard') ? '/dashboard/' : '/';

/**
 * ClerkProvider wrapper — mounted OUTSIDE the Router.
 *
 * IMPORTANT: We intentionally do NOT pass routerPush/routerReplace here.
 * When those callbacks are provided, Clerk's <SignIn> and <SignUp> components
 * push their internal sub-paths (e.g. /signin/factor-two) through react-router,
 * which triggers re-renders of the entire app tree and can cause the Clerk
 * component to lose its internal state (infinite loading spinner).
 *
 * Without routerPush/routerReplace, Clerk uses its own internal navigation
 * for multi-step flows (routing="virtual") and handles post-auth redirects
 * via afterSignInUrl/afterSignUpUrl using window.location (full-page navigation).
 * This is the same pattern that worked before the April 2 refactor.
 */
const ClerkWrapper = ({ children }) => {
  if (!CLERK_PUBLISHABLE_KEY) {
    return children;
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl={ROUTER_BASENAME + 'signin'}
      signUpUrl={ROUTER_BASENAME + 'signup'}
      afterSignOutUrl={window.location.origin + ROUTER_BASENAME + 'signin'}
    >
      {children}
    </ClerkProvider>
  );
};

root.render(
  <Provider store={store}>
    <PostHogProvider client={posthog}>
      <DashboardErrorBoundary>
        {/* <React.StrictMode> */}
        <ClerkWrapper>
          <DashboardAssistantProvider>
            <TooltipProvider>
              <Router basename={ROUTER_BASENAME}>
                <JobsProvider>
                  <DashboardAssistantDockProvider>
                    <App />
                  </DashboardAssistantDockProvider>
                </JobsProvider>
                <Toaster
                  position="bottom-right"
                  toastOptions={{
                    style: {
                      '--normal-text': '#6B6458',
                      color: '#6B6458',
                    },
                    descriptionClassName: 'text-ink-muted',
                    cancelButtonStyle: {
                      color: '#6B6458',
                      backgroundColor: '#F5F3ED',
                    },
                  }}
                />
              </Router>
            </TooltipProvider>
          </DashboardAssistantProvider>
        </ClerkWrapper>
        {/* </React.StrictMode> */}
      </DashboardErrorBoundary>
    </PostHogProvider>
  </Provider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. This has some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
